import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import * as checkoutStore from '@cartwhl/common/stores/checkoutStore';
import * as productStore from '@cartwhl/common/stores/productStore';
import { isPro, isUnlimited } from '@cartwhl/common/stores/userStore';
import { getHomepageUrl } from '@cartwhl/common/utils';
import Text from '@cartwhl/ui/base/Text';
import MinusIcon from '@cartwhl/ui/icons/MinusIcon';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

export default function PurchaseSpinsModal({ close }: { close: () => void }) {
  const { products } = useSnapshot(productStore.state);
  const [quantity, setQuantity] = useState(1);

  const priceObject = productStore.getPriceByID(
    products?.more?.default_price ?? '',
  );
  const spinPrice = priceObject?.unit_amount ?? 500;

  const isPayingCustomer = isPro() || isUnlimited();

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
    }
  }

  const handleCheckout = async () => {
    const hostedCheckoutURL = await checkoutStore.completeHostedCheckout(
      priceObject,
      quantity,
    );
    if (hostedCheckoutURL) {
      window.location.href = hostedCheckoutURL;
    }
  };

  return (
    <Modal
      title="Buy more Spins"
      maxWidth={684}
      open={true}
      onOpenChange={handleOpenChange}
    >
      <Text as="p" variant="studio/body/md">
        Purchase extra Spins anytime. Unused spins will carry over.{' '}
        {!isPayingCustomer ? (
          <>
            Need to buy more?{' '}
            <span
              className="text-blue cursor-pointer underline"
              onClick={() => {
                if (products.pro) {
                  const price = productStore.getPriceByProductAndMode(
                    products.pro.id,
                    'month',
                  );
                  if (price) {
                    checkoutStore.select(price, quantity);
                    checkoutStore.startCheckout();
                    return;
                  }
                }
                window.location.href = getHomepageUrl('/pricing');
              }}
            >
              Upgrade to pro.
            </span>
          </>
        ) : (
          ''
        )}
      </Text>
      <div className="bg-gray-15 dark:bg-gray-75 mt-6 flex items-center justify-between rounded-lg py-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 pl-4">
            {isPayingCustomer && (
              <Button
                className="dark:bg-gray-90 h-8 w-8 bg-white"
                variant="secondary"
                size="sm"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 0}
              >
                <MinusIcon className="h-4 w-4" />
              </Button>
            )}
            <div className="flex min-w-48 items-center justify-center">
              <Text as="span" variant="studio/display/2xl">
                {quantity * productStore.SPINS_PER_PURCHASE}
              </Text>
            </div>
            {isPayingCustomer && (
              <Button
                className="dark:bg-gray-90 h-8 w-8 bg-white "
                variant="secondary"
                size="sm"
                disabled={quantity >= 25}
                onClick={() => setQuantity(quantity + 1)}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            )}
            <div className="pt-8">
              <Text
                as="span"
                variant="studio/body/bold/md"
                className="font-display"
              >
                Spins
              </Text>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 pr-4">
          <Text as="span" variant="studio/body/md">
            Total:
          </Text>
          <Text as="span" variant="studio/body/bold/md">
            ${productStore.formatMoney(quantity * spinPrice)}.00
          </Text>
        </div>
      </div>
      <div className="mt-6 flex flex-row flex-nowrap">
        <Button variant="secondary" onClick={close}>
          Close
        </Button>
        <Button className="ml-auto" variant="primary" onClick={handleCheckout}>
          Checkout
        </Button>
      </div>
    </Modal>
  );
}
